import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, History, Moon, Power, PowerOff, RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import { MeshDeviceDto, MeshPowerAction, MeshPowerOperationDto, MeshPowerOperationStatus } from '../../models';
import { createMeshCentralPowerOperation, getApiError, getMeshCentralPowerOperation, getMeshCentralPowerOperations } from '../../services/api';
import { Button } from '../ui/button';
import { Modal } from '../ui/modal';
import { StatusBadge } from '../ui/status-badge';

const actionDefinitions: Record<MeshPowerAction, {
  title: string;
  description: string;
  icon: typeof Power;
  destructive?: boolean;
}> = {
  wake: { title: 'Разбудить', description: 'Отправить Wake-on-LAN через MeshCentral.', icon: Power },
  reboot: { title: 'Перезагрузить', description: 'Корректно перезапустить выбранное устройство.', icon: RotateCcw },
  shutdown: { title: 'Выключить', description: 'Завершить работу выбранного терминала.', icon: PowerOff, destructive: true },
  sleep: { title: 'Режим сна', description: 'Перевести выбранное устройство в low-power режим.', icon: Moon },
};

const statusDefinitions: Record<MeshPowerOperationStatus, { label: string; variant: 'success' | 'warning' | 'destructive' | 'default' | 'primary' }> = {
  sending: { label: 'Отправляется', variant: 'primary' },
  awaiting_offline: { label: 'Ожидаем отключения', variant: 'warning' },
  awaiting_online: { label: 'Ожидаем Online', variant: 'warning' },
  completed: { label: 'Выполнено', variant: 'success' },
  failed: { label: 'Ошибка', variant: 'destructive' },
  timed_out: { label: 'Не подтверждено', variant: 'default' },
};

interface Props { device: MeshDeviceDto }

export const MeshPowerControl = ({ device }: Props) => {
  const [operations, setOperations] = useState<MeshPowerOperationDto[]>([]);
  const [confirmAction, setConfirmAction] = useState<Exclude<MeshPowerAction, 'wake'>>();
  const [confirmation, setConfirmation] = useState('');
  const [busyAction, setBusyAction] = useState<MeshPowerAction>();
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const confirmationText = device.name || device.hostname || device.nodeId;

  const loadHistory = useCallback(async () => {
    try {
      setOperations((await getMeshCentralPowerOperations(device.nodeId)).data.data);
      setError('');
    } catch (e) {
      setError(getApiError(e));
    }
  }, [device.nodeId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    const timer = window.setInterval(loadHistory, 30_000);
    return () => window.clearInterval(timer);
  }, [loadHistory]);

  const activeOperation = useMemo(() => operations.find((item) => ['sending', 'awaiting_offline', 'awaiting_online'].includes(item.status)), [operations]);
  useEffect(() => {
    if (!activeOperation) return;
    const poll = async () => {
      try {
        const updated = (await getMeshCentralPowerOperation(activeOperation.id)).data.data;
        setOperations((current) => current.map((item) => item.id === updated.id ? updated : item));
        setError('');
        if (updated.status === 'completed') setAnnouncement(`${actionDefinitions[updated.operation].title}: состояние устройства подтвердило выполнение.`);
        if (updated.status === 'timed_out') setAnnouncement('Команда была принята, но изменение статуса не подтверждено.');
      } catch (e) {
        setError(getApiError(e));
      }
    };
    const timer = window.setInterval(poll, 5_000);
    return () => window.clearInterval(timer);
  }, [activeOperation]);

  const execute = async (action: MeshPowerAction) => {
    setBusyAction(action);
    setError('');
    setAnnouncement('');
    try {
      const response = await createMeshCentralPowerOperation(device.nodeId, action, action === 'wake' ? undefined : confirmation);
      const operation = response.data.data;
      setOperations((current) => [operation, ...current.filter((item) => item.id !== operation.id)].slice(0, 10));
      setAnnouncement(`${actionDefinitions[action].title}: MeshCentral принял команду. Проверяем статус терминала.`);
      setConfirmAction(undefined);
      setConfirmation('');
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setBusyAction(undefined);
    }
  };

  const requestAction = (action: MeshPowerAction) => {
    if (action === 'wake') execute(action);
    else { setConfirmation(''); setConfirmAction(action); }
  };

  return <div className="space-y-5">
    <div>
      <h3 className="text-lg font-semibold">Управление этим устройством</h3>
      <p className="mt-1 text-sm text-muted-foreground">Каждая команда привязана к Node ID открытого терминала и записывается в аудит.</p>
    </div>

    <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
      <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" /><div className="min-w-0"><p className="font-semibold text-cyan-950">{confirmationText}</p><p className="mt-1 break-all font-mono text-[11px] text-cyan-800">{device.nodeId}</p></div></div>
    </div>

    <div aria-live="polite" className="space-y-2">
      {error && <div role="alert" className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {announcement && <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary">{announcement}</div>}
    </div>

    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {(Object.keys(actionDefinitions) as MeshPowerAction[]).map((action) => {
        const definition = actionDefinitions[action];
        const Icon = definition.icon;
        const unavailable = action === 'wake' ? device.online : !device.online;
        return <button key={action} type="button" disabled={unavailable || Boolean(busyAction) || Boolean(activeOperation)} onClick={() => requestAction(action)} className="group cursor-pointer rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-colors hover:border-cyan-300 hover:bg-cyan-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-45">
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${definition.destructive ? 'bg-destructive/10 text-destructive' : 'bg-cyan-50 text-cyan-700'}`}><Icon className="h-5 w-5" /></span>
          <p className="mt-3 font-semibold">{definition.title}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{definition.description}</p>
          {unavailable && <p className="mt-2 text-[11px] font-semibold text-muted-foreground">{action === 'wake' ? 'Терминал уже online' : 'Терминал offline'}</p>}
        </button>;
      })}
    </div>

    <section className="overflow-hidden rounded-xl border border-border">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/35 px-4 py-3"><div className="flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /><h4 className="text-sm font-semibold">История этого терминала</h4></div><Button size="sm" variant="ghost" onClick={loadHistory}><RefreshCw className="h-4 w-4" />Обновить</Button></div>
      {operations.length === 0 ? <p className="px-4 py-8 text-center text-sm text-muted-foreground">Команд ещё не было.</p> : <div className="divide-y divide-border">{operations.map((operation) => {
        const status = statusDefinitions[operation.status];
        return <div key={operation.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div><p className="text-sm font-semibold">{actionDefinitions[operation.operation].title}</p><p className="mt-1 text-xs text-muted-foreground">{formatDate(operation.requestedAt)} · {operation.requestedBy}</p></div>
          <StatusBadge label={status.label} variant={status.variant} live={['sending', 'awaiting_offline', 'awaiting_online'].includes(operation.status)} />
        </div>;
      })}</div>}
    </section>

    <Modal open={Boolean(confirmAction)} onClose={() => { if (!busyAction) setConfirmAction(undefined); }} title={`${confirmAction ? actionDefinitions[confirmAction].title : 'Подтвердить'}: ${confirmationText}`} maxWidth="max-w-lg" footer={<>
      <Button variant="ghost" onClick={() => setConfirmAction(undefined)} disabled={Boolean(busyAction)}>Отмена</Button>
      <Button variant={confirmAction === 'shutdown' ? 'destructive' : 'default'} disabled={!confirmAction || confirmation !== confirmationText || Boolean(busyAction)} onClick={() => confirmAction && execute(confirmAction)}>
        {confirmAction && actionDefinitions[confirmAction].title}
      </Button>
    </>}>
      <div className="space-y-4">
        <div className="flex gap-3 rounded-xl border border-warning/25 bg-warning/10 p-4 text-sm"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" /><p>Команда будет отправлена только на <strong>{confirmationText}</strong>. Активные медосмотры и сеансы могут быть прерваны.</p></div>
        <label htmlFor="mesh-power-confirmation" className="block text-sm font-medium">Введите имя терминала для подтверждения
          <input id="mesh-power-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" className="mt-2 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm text-slate-950 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
        </label>
        <p className="break-all rounded-lg bg-muted/50 px-3 py-2 font-mono text-xs text-muted-foreground">{confirmationText}</p>
      </div>
    </Modal>
  </div>;
};

const formatDate = (value: string) => new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
